import { createElement, lazy, useState, type ComponentType } from "react";

/**
 * Un composant chargé à la demande (son propre morceau de code). `load()` le fait partir plus tôt
 * (au démarrage, au survol…). Une fois chargé, il s'affiche sans passer par Suspense : React 19
 * retient un contenu suspendu au moins 300 ms avant de le montrer, même quand le morceau arrive
 * juste après.
 */
export type Load = () => Promise<unknown>;

export function lazyComponent<M extends Record<K, ComponentType<never>>, K extends keyof M>(importer: () => Promise<M>, name: K): M[K] & { load: Load } {
  let ready: ComponentType<object> | null = null;
  let pending: Promise<ComponentType<object>> | null = null;
  const load = () =>
    (pending ??= importer().then(
      (m) => (ready = m[name] as unknown as ComponentType<object>),
      (e: unknown) => {
        pending = null; // un morceau qui n'a pas pu venir (réseau) sera redemandé
        throw e;
      },
    ));
  const Lazy = lazy(async () => ({ default: await load() }));
  // Le type choisi au premier rendu reste celui de l'instance : passer de Lazy au composant chargé la remontait.
  const Component = (props: object) => {
    const [type] = useState<ComponentType<object>>(() => ready ?? Lazy);
    return createElement(type, props);
  };
  return Object.assign(Component, { load }) as unknown as M[K] & { load: Load };
}
